/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { PDFDocument } from 'pdf-lib'

// pdf.js 4.2.67 (correção da CVE-2024-4367) é distribuído apenas como módulo ES
// com top-level await, que o webpack 4 não consegue bundlar. Por isso a lib é
// servida estaticamente em /pdfjs e carregada em runtime como módulo nativo.
// O build "legacy" define globalThis.pdfjsLib / globalThis.pdfjsLibPromise.
const PDFJS_LIB_URL = '/pdfjs/pdf.min.mjs'
const PDFJS_WORKER_URL = '/pdfjs/pdf.worker.min.mjs'

let pdfjsLibPromise: Promise<any> | null = null

function loadPdfjsLib(): Promise<any> {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = new Promise<any>((resolve, reject) => {
      const script = document.createElement('script')
      script.type = 'module'
      script.src = PDFJS_LIB_URL
      script.onload = async () => {
        try {
          const lib = await ((window as any).pdfjsLibPromise || (window as any).pdfjsLib)
          if (!lib) {
            reject(new Error('pdf.js carregado, mas pdfjsLib não foi exposto'))
            return
          }
          lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL
          resolve(lib)
        } catch (err) {
          reject(err)
        }
      }
      script.onerror = () => {
        pdfjsLibPromise = null
        reject(new Error(`Falha ao carregar ${PDFJS_LIB_URL}`))
      }
      document.head.appendChild(script)
    })
  }
  return pdfjsLibPromise
}

async function compressPdfAsImage(pdfBytes: ArrayBuffer): Promise<Uint8Array> {
  return new Promise(async (resolve) => {
    const pdfjsLib = await loadPdfjsLib()
    // isEvalSupported: false desabilita o uso de new Function() na geração de
    // glifos (vetor da CVE-2024-4367) e mantém compatibilidade com a CSP sem
    // 'unsafe-eval'. O buffer é copiado pois o pdf.js transfere os dados para
    // o worker, e o pdf-lib ainda precisa ler o buffer original.
    const pdf = await pdfjsLib.getDocument({
      data: new Uint8Array(pdfBytes.slice(0)),
      isEvalSupported: false,
    }).promise
    const originalPdf = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
    })

    const newPdf = await PDFDocument.create()

    // Copia os metadados
    if (originalPdf.getTitle()) newPdf.setTitle(originalPdf.getTitle()!)
    if (originalPdf.getAuthor()) newPdf.setAuthor(originalPdf.getAuthor()!)
    if (originalPdf.getSubject()) newPdf.setSubject(originalPdf.getSubject()!)
    if (originalPdf.getCreator()) newPdf.setCreator(originalPdf.getCreator()!)
    if (originalPdf.getProducer()) newPdf.setProducer(originalPdf.getProducer()!)

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum)
      const textContent = await page.getTextContent()
      const operators = await page.getOperatorList()

      const hasText = textContent.items.length > 0
      const hasImages = operators.fnArray.some((op: number) => op === pdfjsLib.OPS.paintImageXObject)
      const isTextOnly = hasText && !hasImages

      if (!isTextOnly) {
        const originalPage = originalPdf.getPages()[pageNum - 1]
        const originalWidth = originalPage.getWidth()
        const originalHeight = originalPage.getHeight()

        const scale = 4.0
        const viewport = page.getViewport({ scale })

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        canvas.width = viewport.width
        canvas.height = viewport.height

        await page.render({ canvasContext: ctx, viewport }).promise
        const imgData = canvas.toDataURL('image/jpeg', 0.1)
        const img = await newPdf.embedJpg(imgData)

        const newPage = newPdf.addPage([originalWidth, originalHeight])
        newPage.drawImage(img, {
          x: 0,
          y: 0,
          width: originalWidth,
          height: originalHeight,
        })
      } else {
        const [copiedPage] = await newPdf.copyPages(originalPdf, [pageNum - 1])
        newPdf.addPage(copiedPage)
      }
    }

    const compressedPdfBytes = await newPdf.save({
      useObjectStreams: true,
      addDefaultPage: false,
      objectsPerTick: 100,
      updateFieldAppearances: false,
    })
    resolve(compressedPdfBytes)
  })
}


export default async function processPdfFile(file: File): Promise<File | null> {
  return new Promise(async (resolve) => {
    const originalPdfBytes = await file.arrayBuffer()

    try {
      const compressedPdfBytes = await compressPdfAsImage(originalPdfBytes)

      if (compressedPdfBytes.byteLength > originalPdfBytes.byteLength) {
        const blob = new Blob([originalPdfBytes as any], { type: 'application/pdf' })
        return resolve(blob ? new File([blob], file.name, { type: 'application/pdf' }) : null)
      }

      const blob = new Blob([compressedPdfBytes as any], { type: 'application/pdf' })
      resolve(blob ? new File([blob], file.name, { type: 'application/pdf' }) : null)
    } catch (err) {
      const blob = new Blob([originalPdfBytes as any], { type: 'application/pdf' })
      return resolve(blob ? new File([blob], file.name, { type: 'application/pdf' }) : null)
    }
  })
}
